/**
 * Aday Sınav Erişim — Spec Bölüm 7.2 + S2.T7
 *
 * Aday firma yöneticisinden aldığı kodu (örn. CENTRI-K7P2) girer.
 * Doğru ise preview gösterilir → "Kayıt Ol ve Başla" → /sinav/kayit
 */
import { useState } from 'react';
import { useLocation } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Button, Card, Field, Input } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';

interface ExamPreview {
  examId: string;
  accessCode: string;
  organization: { name: string; industry: string };
  questionCount: number;
  durationMin: number;
  anonymousMode: boolean;
  validUntil: string;
}

const INDUSTRY_LABELS: Record<string, string> = {
  MACHINERY: 'Makine İmalat',
  AUTOMOTIVE: 'Otomotiv',
  DEFENSE: 'Savunma Sanayi',
  CONSUMER: 'Tüketim Ürünleri',
  SHEET_FAB: 'Sac Metal İmalat',
  PRECISION_MECHANICAL: 'Hassas Mekanik',
  OTHER: 'Diğer',
};

export function ExamAccessPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ExamPreview | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const normalized = code.trim().toUpperCase();
      if (!/^[A-Z0-9]{2,10}-[A-Z0-9]{4}$/.test(normalized)) {
        setError(
          'Erişim kodu formatı: 2-10 alfanumerik, tire, 4 karakter (örn. CENTRI-K7P2)',
        );
        return;
      }
      const data = await api.get<ExamPreview>(`/api/public/exam/${normalized}`);
      setPreview(data);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Sunucuya ulaşılamadı');
      }
    } finally {
      setLoading(false);
    }
  }

  function startRegistration(): void {
    if (!preview) return;
    // S2.T7: Aday kayıt formuna geçiş
    setLocation(`/sinav/kayit?code=${encodeURIComponent(preview.accessCode)}`);
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-md px-6 py-12">
        <Card>
          <h1 className="mb-2 text-2xl font-bold text-slate-900">
            Sınav Erişim Kodu
          </h1>
          <p className="mb-6 text-sm text-slate-600">
            Firma yöneticinizden aldığınız kodu girin. Örn:{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">CENTRI-K7P2</code>
          </p>

          {error && (
            <div className="mb-4">
              <Alert variant="danger">{error}</Alert>
            </div>
          )}

          {!preview && (
            <form onSubmit={handleSubmit} noValidate>
              <Field label="Erişim Kodu" htmlFor="code" required>
                <Input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="CENTRI-K7P2"
                  autoComplete="off"
                  autoCapitalize="characters"
                  className="text-center text-lg tracking-wider"
                />
              </Field>
              <Button
                type="submit"
                loading={loading}
                disabled={code.length < 5}
                className="w-full"
              >
                Sınavı Bul
              </Button>
              <p className="mt-4 text-center text-sm text-slate-600">
                Hızlıca denemek mi istiyorsunuz?{' '}
                <a
                  href="/demo"
                  className="font-medium text-blue-900 hover:underline"
                >
                  Demo modu (5 soru)
                </a>
              </p>
            </form>
          )}

          {preview && (
            <div>
              <Alert variant="success">
                Sınav bulundu! Bilgileri kontrol edin.
              </Alert>
              <dl className="mt-4 space-y-3 text-sm">
                <Row label="Firma" value={preview.organization.name} />
                <Row
                  label="Sektör"
                  value={
                    INDUSTRY_LABELS[preview.organization.industry] ??
                    preview.organization.industry
                  }
                />
                <Row label="Soru Sayısı" value={String(preview.questionCount)} />
                <Row label="Süre" value={`${preview.durationMin} dakika`} />
                <Row
                  label="Anonim Mod"
                  value={preview.anonymousMode ? 'Açık (KVKK güvende)' : 'Kapalı'}
                />
              </dl>
              <Button
                onClick={startRegistration}
                className="mt-6 w-full"
              >
                Kayıt Ol ve Sınava Başla
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setPreview(null);
                  setCode('');
                }}
                className="mt-2 w-full"
              >
                Farklı bir kod gir
              </Button>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
      <dt className="text-slate-600">{label}</dt>
      <dd className="font-medium text-slate-900">{value}</dd>
    </div>
  );
}
